import { Card, CardContent, CardHeader, Typography, Grid2 as Grid, Stack, alpha, Divider } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid-premium';
import React, { useMemo } from 'react';
import { ListChildComponentProps } from 'react-window';
import { useTranslation } from 'react-i18next';
import { Dialogs } from '../../constants/Dialogs';
import { openDialog } from '../../ducks/dialogSlice';
import { useAppSelector, useAppDispatch } from '../../hooks';
import { setDealerDialog } from '../../ducks/dealersSlice';
import { getActionsMenuProps, getColumnCellRenderer } from '../../utils/MUIDataGridUtils';
import { MUIDataGrid, MUIDataGridColumn } from '../../constants/MUIDataGrid';
import { ActionsMenu } from '../ActionsMenu';
import { I18nKeys } from '../../constants/I18nKeys';
import { kebabCaseToCamelCase } from '../../utils/stringUtils';

export const ConfiguratorsCard: React.FC<ListChildComponentProps> = ({ style, data: configurators, index }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const configurator = configurators[index];
  const clientId = useAppSelector((state) => state?.clientData.clientId);

  const switches = useMemo(
    () =>
      [
        MUIDataGridColumn.Pricing,
        MUIDataGridColumn.DealerNetwork,
        MUIDataGridColumn.Template,
        MUIDataGridColumn.SalesTools,
        MUIDataGridColumn.ProServicesBranch,
      ].map((column) => ({
        label: t(`table-header-${column}`),
        component: getColumnCellRenderer(
          {
            table: MUIDataGrid.Configurators,
            editable: false,
            supplierOnly: column === MUIDataGridColumn.Pricing,
          },
          column,
          dispatch,
          t,
        )?.renderCell?.({
          row: configurator,
          value: configurator[kebabCaseToCamelCase(`${column}-enabled`)],
        } as GridRenderCellParams),
      })),
    [configurator, t, dispatch],
  );

  return (
    <Card
      elevation={0}
      key={configurator.key}
      style={style}
      onClick={(): void => {
        dispatch(setDealerDialog({ dialogDealer: configurator, dialogClientId: clientId }));
        dispatch(openDialog({ dialog: Dialogs.Dealer }));
      }}
      sx={{
        py: '16px',
        borderRadius: '8px',
        height: 'fit-content !important',
        ml: '24px',
        width: 'calc(100% - 48px) !important',
        backgroundColor: alpha('#F2F4F5', 0.8),
      }}
    >
      <Stack direction="column" spacing="8px">
        <CardHeader
          title={
            <Typography variant="body1" fontWeight={600}>
              {configurator.name}
            </Typography>
          }
          sx={{ height: '24px', px: '16px', '& button': { position: 'absolute', top: '2px', right: '2px' } }}
          action={
            <ActionsMenu
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...getActionsMenuProps(
                {
                  table: MUIDataGrid.Configurators,
                },
                configurator,
                dispatch,
                t,
              )}
            />
          }
        />
        <CardContent sx={{ p: '0px !important' }}>
          <Grid container spacing="16px">
            <Grid size={12} px="16px">
              <Typography color="textSecondary" variant="body2">
                {`${
                  configurator.key === 'carportview'
                    ? t(I18nKeys.ConfiguratorFormFieldConfiguratorCarports)
                    : t(I18nKeys.ConfiguratorFormFieldConfiguratorSheds)
                } ${
                  configurator?.suppliers?.length
                    ? t(I18nKeys.ConfiguratorFormFieldDealer)
                    : t(I18nKeys.ConfiguratorFormFieldSupplier)
                }`}
              </Typography>
            </Grid>
            <Divider sx={{ width: '100%' }} />
            <Grid container spacing="8px" px="16px">
              {switches.map(({ label, component }) => (
                <Grid container size={4} justifyContent="left">
                  <Stack direction="column" alignItems="start">
                    <Typography variant="body2" color="textSecondary">
                      {label}
                    </Typography>
                    {component}
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </CardContent>
      </Stack>
    </Card>
  );
};
